import { useState, useRef, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { uid } from "../utils/helpers";
import { createPortal } from "react-dom";

export default function LinkGenView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  const emptyRow = () => ({ id: uid(), topic: "", anchor1: "", url1: "", anchor2: "", url2: "", chars: 3000, lang: "uk", status: "", result: "", date: "" });
  const storeKey = activeId || "_linkgen";
  const stored = dataRef.current[storeKey + "_links"];
  const pendingKey = storeKey + "_linkgen_pending"; // ключі, надіслані з модуля «Позиції»
  const hadPending = Array.isArray(dataRef.current[pendingKey]) && dataRef.current[pendingKey].length > 0;
  const [rows, setRowsRaw] = useState(() => {
    const pending = dataRef.current[pendingKey];
    const base = stored?.rows?.length ? stored.rows : [];
    if (Array.isArray(pending) && pending.length) {
      // Ключі вже розкладені по статтях (anchor1/url1 + anchor2/url2) — лишається дозаповнити поля.
      const pend = pending.map(p => ({ ...emptyRow(), topic: p.topic || "", anchor1: p.anchor1 || "", url1: p.url1 || "", anchor2: p.anchor2 || "", url2: p.url2 || "" }));
      const keep = base.filter(r => r.topic || r.anchor1 || r.anchor2 || r.result); // не губимо наявну роботу
      return [...pend, ...keep];
    }
    return base.length ? base : [emptyRow(), emptyRow(), emptyRow()];
  });
  const [projectName, setProjectName] = useState(() => stored?.projectName || "");
  // Споживаємо pending один раз: фіксуємо рядки у сторі й чистимо, щоб повторний вхід не дублював.
  useEffect(() => {
    if (hadPending) { delete dataRef.current[pendingKey]; dataRef.current[storeKey + "_links"] = { rows, projectName }; }
  }, []);
  const runRef = useRef(false);
  const [, forceUpdate] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [archive, setArchive] = useState([]);
  const [archiveOpen, setArchiveOpen] = useState(null);
  const [anchorBank, setAnchorBank] = useState([]);
  const [showAnchorBank, setShowAnchorBank] = useState(false);
  const [anchorSearch, setAnchorSearch] = useState("");
  const [semKw, setSemKw] = useState([]);   // {keyword, url} із семантики (clusters) для автокомпліту анкора
  const [posMap, setPosMap] = useState({}); // keyword(lower) → позиція (з останнього знімку позицій)
  const [addN, setAddN] = useState(10);     // масове додавання рядків
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // статті, обрані для CSV-експорту

  // Load saved articles from DB
  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/seo-texts`)
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        // seo-texts table has link_articles too — filter by type or load from link route
      }).catch(() => {});
    // Load anchor bank
    apiFetch(`/api/projects/${activeId}/anchors`)
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (Array.isArray(d)) setAnchorBank(d); }).catch(() => {});
    // Load semantics (clusters) → keyword→url suggestions for the anchor autocomplete
    apiFetch(`/api/projects/${activeId}/clusters`)
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        if (!Array.isArray(d)) return;
        const seen = new Set(); const out = [];
        for (const c of d) {
          if (!c.url) continue;
          const kws = [];
          if (c.hfKeyword) kws.push(c.hfKeyword);
          (c.keywords || []).forEach(k => { const kw = typeof k === "string" ? k : (k && k.keyword); if (kw) kws.push(kw); });
          for (const kw of kws) {
            const key = String(kw).toLowerCase().trim();
            if (!key || seen.has(key)) continue;
            seen.add(key); out.push({ keyword: kw, url: c.url });
          }
        }
        setSemKw(out);
      }).catch(() => {});
    // Load positions → keyword→position для показу позиції ключа у випадашці анкора
    apiFetch(`/api/projects/${activeId}/positions`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const snaps = (d && d.snapshots) || [];
        const last = snaps[snaps.length - 1];
        const m = {};
        const posKw = [];
        (last?.keywords || []).forEach(k => {
          if (k && k.keyword) {
            const key = String(k.keyword).toLowerCase().trim();
            m[key] = k.position;
            posKw.push({ keyword: k.keyword, url: k.url || k.serpUrl || "" });
          }
        });
        setPosMap(m);
        // Додаємо ключі зі знімка позицій у автокомпліт анкора (напр. ті, яких нема в clusters — «розлучення» тощо).
        setSemKw(prev => {
          const seen = new Set((prev || []).map(x => String(x.keyword).toLowerCase().trim()));
          const add = [];
          for (const it of posKw) { const key = String(it.keyword).toLowerCase().trim(); if (key && !seen.has(key)) { seen.add(key); add.push(it); } }
          return add.length ? [...(prev || []), ...add] : prev;
        });
      }).catch(() => {});
    // Load from links route
    apiFetch(`/api/projects/${activeId}/links`)
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        if (Array.isArray(d) && d.length) {
          const saved = d.filter(r => r.status === "done" && r.result);
          if (saved.length) setArchive(saved);
          // Restore rows from DB if dataRef is empty (e.g. after page refresh)
          const storedNow = dataRef.current[storeKey + "_links"];
          if (!storedNow?.rows?.length) {
            const allRows = d.filter(r => r.topic || r.anchor1 || r.anchor2);
            if (allRows.length) setRows(allRows);
          }
        }
      }).catch(() => {});
  }, [activeId, apiFetch]);

  // Persist rows to dataRef on every change
  const setRows = (updater) => {
    setRowsRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      dataRef.current[storeKey + "_links"] = { rows: next, projectName };
      return next;
    });
  };
  const saveProjName = (name) => {
    setProjectName(name);
    dataRef.current[storeKey + "_links"] = { ...dataRef.current[storeKey + "_links"], projectName: name };
  };
  const rerender = () => forceUpdate(n => n + 1);

  const updateRow = (id, updates) => setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  const addRow = () => setRows(prev => [...prev, emptyRow()]);
  const delRow = (id) => {
    setRows(prev => {
      const next = prev.filter(r => r.id !== id);
      if (activeId) saveLinks(activeId, next);
      return next;
    });
    setArchive(prev => prev.filter(a => a.id !== id));
  };

  const generateArticle = async (row, index, total, prevTitles) => {
    const projCtx = projectName ? `\n\nПРОЕКТ/БРЕНД: "${projectName}"
ПРАВИЛО БРЕНДА: Название "${projectName}" ОБЯЗАТЕЛЬНО должно появиться в тексте минимум 3 раза: в первом абзаце, в середине и ближе к концу. Бренд должен быть представлен как авторитетный источник/эксперт в теме статьи.` : "";

    const uniqueCtx = total > 1 ? `\n\nУНИКАЛЬНОСТЬ: Это статья ${index + 1} из ${total}. Каждая статья ДОЛЖНА быть полностью уникальной!
- Используй ДРУГУЮ структуру H2/H3 заголовков (не повторяй шаблоны)
- Начни статью нестандартно — НЕ с определения термина
- Подойди к теме под ДРУГИМ углом (практический / аналитический / сравнительный / в формате гайда / через историю / через проблему-решение)
- Придумай ОРИГИНАЛЬНЫЕ заголовки, не используй клише типа "Что такое...", "Преимущества...", "Как выбрать..."${prevTitles.length ? `\n- НЕ ИСПОЛЬЗУЙ эти заголовки H2, они уже были в предыдущих статьях: ${prevTitles.map(t => `"${t}"`).join(", ")}` : ""}` : "";

    const domainName = proj?.domain ? proj.domain.replace(/^https?:\/\//, "").replace(/\/+$/, "") : "";
    const domainCtx = domainName ? `\nДОМЕН САЙТУ: ${domainName} — при необходимости упомяни домен как источник.` : "";

    const angles = ["практичний гайд з покроковими порадами", "аналітичний огляд з фактами та статистикою", "порівняльний аналіз варіантів", "розв'язання конкретної проблеми читача", "експертний огляд з прикладами з практики", "формат «питання-відповідь» з детальними поясненнями", "топ-список з обґрунтуванням кожного пункту"];
    const angle = angles[(index || 0) % angles.length];

    const prompt = `Ти — досвідчений копірайтер-експерт. Напиши УНІКАЛЬНУ SEO-статтю.

ТЕМА: ${row.topic}
МОВА: ${row.lang === "uk" ? "Українська" : row.lang === "ru" ? "Русский" : row.lang === "pl" ? "Polski" : "English"}
ОБСЯГ: ${row.chars} символів (±10%)
ПІДХІД/КУТ: ${angle}${projCtx}${domainCtx}${uniqueCtx}${proj?.version ? `\nВЕРСІЯ САЙТУ: ${proj.version}` : ""}

ОБОВ'ЯЗКОВІ АНКОРНІ ПОСИЛАННЯ (встав РІВНО 2 посилання в текст):
1. Анкор: "${row.anchor1}" → URL: ${row.url1}
2. Анкор: "${row.anchor2}" → URL: ${row.url2}

ПРАВИЛА:
- Стаття ОБОВ'ЯЗКОВО повинна бути корисною, інформативною, з конкретними порадами, фактами чи прикладами
- Посилання мають бути вписані ПРИРОДНО в контекст статті
- Посилання розміщуй в РІЗНИХ частинах статті (НЕ поруч)
- Формат посилань: <a href="URL">анкор</a>
- Використовуй 3-5 підзаголовків H2 та H3 для структури (кожен заголовок УНІКАЛЬНИЙ, конкретний, без кліше)
- НЕ згадуй що це SEO-стаття або що посилання рекламні
- Пиши як реальний експерт в темі, з конкретикою
- Поточний рік: ${new Date().getFullYear()}. Якщо згадуєш рік — використовуй ТІЛЬКИ ${new Date().getFullYear()}
- ЗАБОРОНЕНО згадувати Росію, російські бренди, російські компанії, російські міста. Контекст — УКРАЇНА. Якщо потрібні приклади країн — використовуй Україну, країни ЄС, США

Поверни ТІЛЬКИ текст статті в HTML форматі (з <h2>, <h3>, <p>, <a>, <ul> тегами). Без обгорток, без \`\`\`, без пояснень.`;

    const _tkn2 = localStorage.getItem("metago_token");
    const resp = await fetch("/api/claude", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(_tkn2 ? { Authorization: `Bearer ${_tkn2}` } : {}) },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 4096,
        temperature: 0.9,
        messages: [{ role: "user", content: prompt }]
      })
    });
    if (!resp.ok) {
      const errText = await resp.text().catch(() => "");
      throw new Error(`API ${resp.status}: ${errText.slice(0, 100)}`);
    }
    const data = await resp.json();
    const text = data.content?.find(b => b.type === "text")?.text || "";
    if (!text) throw new Error("Пустой ответ от API");
    return text.replace(/```html\s*/gi, "").replace(/```/g, "").trim();
  };

  const runAll = async () => {
    const valid = rows.filter(r => r.topic && r.anchor1 && r.url1 && r.anchor2 && r.url2);
    if (!valid.length) return;
    runRef.current = true; rerender();
    const prevTitles = [];

    for (let i = 0; i < valid.length; i++) {
      const row = valid[i];
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "generating" } : r));
      try {
        const html = await generateArticle(row, i, valid.length, prevTitles);
        // Extract H2 titles to avoid repeating in next articles
        const h2matches = html.match(/<h2[^>]*>(.*?)<\/h2>/gi) || [];
        h2matches.forEach(m => {
          const txt = m.replace(/<[^>]+>/g, "").trim();
          if (txt) prevTitles.push(txt);
        });
        const now = new Date().toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, result: html, status: "done", date: now } : r));
      } catch (err) {
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "error", result: "Ошибка: " + err.message } : r));
      }
    }
    runRef.current = false; rerender();
    // Save links to DB + anchors to bank
    if (activeId) setRows(prev => {
      saveLinks(activeId, prev);
      // Update archive
      const doneItems = prev.filter(r => r.status === "done" && r.result);
      setArchive(old => {
        const ids = new Set(old.map(o => o.id));
        return [...old, ...doneItems.filter(d => !ids.has(d.id))];
      });
      // Save anchors to bank
      const anchors = [];
      doneItems.forEach(r => {
        if (r.anchor1 && r.url1) anchors.push({ anchor: r.anchor1, url: r.url1 });
        if (r.anchor2 && r.url2) anchors.push({ anchor: r.anchor2, url: r.url2 });
      });
      if (anchors.length) {
        apiFetch(`/api/projects/${activeId}/anchors`, {
          method: "POST", body: JSON.stringify({ anchors }),
        }).then(r => r.json()).then(() => {
          // Reload bank
          apiFetch(`/api/projects/${activeId}/anchors`).then(r => r.json()).then(d => { if (Array.isArray(d)) setAnchorBank(d); });
        }).catch(() => {});
      }
      return prev;
    });
  };

  // ── Експорт обраних статей у CSV-шаблон "Importing Publications" ──
  // Коди мов цільової платформи (укр = "ua", не "uk").
  const LANG_IMPORT_CODE = { uk: "ua", ua: "ua", ru: "ru", en: "en", pl: "pl" };
  const toImportLang = (l) => LANG_IMPORT_CODE[String(l || "").toLowerCase()] || "en";
  // Транслітерація теми у slug для необов'язкової колонки "Desired URL".
  const SLUG_MAP = { "а":"a","б":"b","в":"v","г":"h","ґ":"g","д":"d","е":"e","є":"ie","ж":"zh","з":"z","и":"y","і":"i","ї":"i","й":"i","к":"k","л":"l","м":"m","н":"n","о":"o","п":"p","р":"r","с":"s","т":"t","у":"u","ф":"f","х":"kh","ц":"ts","ч":"ch","ш":"sh","щ":"shch","ь":"","ю":"iu","я":"ia","ё":"e","ы":"y","э":"e","ъ":"" };
  const slugify = (s) => String(s || "").toLowerCase().trim()
    .replace(/[а-яёіїєґ]/g, (ch) => (ch in SLUG_MAP ? SLUG_MAP[ch] : ""))
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
  // RFC-4180: лапки лише за потреби, внутрішні подвоюємо.
  const csvCell = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const exportSelectedCsv = () => {
    const done = rows.filter((r) => r.status === "done" && r.result);
    let picked = selectedIds.size ? done.filter((r) => selectedIds.has(r.id)) : done;
    if (!picked.length) { flash("Нет выбранных готовых статей"); return; }
    if (picked.length > 150) { flash("Платформа принимает максимум 150 статей — выгружаю первые 150 из " + picked.length); picked = picked.slice(0, 150); }
    const header = ["Article Title*", "Desired URL", "Publication text, HTML*", "Article language*", "Title", "Descripton", "Key Keywords"];
    const lines = [header.map(csvCell).join(",")];
    picked.forEach((r) => {
      const keywords = [r.anchor1, r.anchor2].filter(Boolean).join("; ");
      lines.push([r.topic || "", slugify(r.topic), r.result || "", toImportLang(r.lang), "", "", keywords].map(csvCell).join(","));
    });
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    if (blob.size > 10 * 1024 * 1024) { flash("⚠️ Файл больше 10 MB — платформа не примет. Выгрузите меньше статей."); return; }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const nm = (projectName || proj?.name || activeId || "publications").replace(/[^a-zа-яёіїєґ0-9]+/gi, "-").replace(/^-|-$/g, "").slice(0, 40);
    a.download = "publications-" + nm + ".csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    flash(picked.length + " статей выгружено в CSV");
  };

  const exportAll = () => {
    const done = rows.filter(r => r.status === "done" && r.result);
    if (!done.length) { flash("Нет готовых статей"); return; }
    done.forEach(r => {
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${r.topic}</title><style>body{font-family:Arial,sans-serif;max-width:800px;margin:40px auto;padding:0 20px;line-height:1.7;color:#333}h2{color:#1a1a2e;margin-top:30px}h3{color:#444}a{color:#6366f1}</style></head><body>${r.result}</body></html>`;
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = r.topic.slice(0, 50).replace(/[^a-zа-яёіїєґ0-9\s]+/gi, "").replace(/\s+/g, "-") + ".html";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    });
    flash(`${done.length} файлов скачано`);
  };

  const copyToClipboard = (text) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;left:-9999px;top:-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      flash("Скопировано ✓");
    } catch(e) {
      // fallback
      navigator.clipboard?.writeText(text).then(() => flash("Скопировано ✓")).catch(() => flash("Ошибка копирования"));
    }
  };

  const inpSm = { ...inp, fontSize: 12, padding: "6px 8px" };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>🔗 Генерация статей для ссылок</h2>
      <p style={{ ...muted, marginBottom: 16 }}>Заполните таблицу → сгенерируйте статьи с анкорными ссылками</p>

      {/* Project name */}
      <div style={{ ...card, padding: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
        <label style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>📌 Проект / Бренд:</label>
        <input value={projectName} onChange={e => saveProjName(e.target.value)} placeholder="Название проекта (будет упомянуто в статьях)" style={{ ...inp, fontSize: 12, flex: 1 }} />
      </div>

      {/* Articles input */}
      {rows.map((r, i) => (
        <div key={r.id} style={{ ...card, padding: 12, marginBottom: 8, border: r.status === "done" ? `2px solid #22c55e` : r.status === "generating" ? `2px solid #6366f1` : undefined }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            {r.status === "done" && r.result && (
              <input type="checkbox" checked={selectedIds.has(r.id)} title="Обрати для CSV-експорту"
                onChange={(e) => setSelectedIds((prev) => { const n = new Set(prev); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })}
                style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#22c55e" }} />
            )}
            <span style={{ fontSize: 13, fontWeight: 700, color: "#6366f1" }}>#{i + 1}</span>
            <input value={r.topic} onChange={e => updateRow(r.id, { topic: e.target.value })} placeholder="Тема статьи" style={{ ...inpSm, flex: 1, fontWeight: 600 }} />
            <select value={r.lang} onChange={e => updateRow(r.id, { lang: e.target.value })} style={{ ...inpSm, width: 55 }}>
              <option value="uk">UA</option>
              <option value="ru">RU</option>
              <option value="en">EN</option>
              <option value="pl">PL</option>
            </select>
            <input type="number" value={r.chars} onChange={e => updateRow(r.id, { chars: parseInt(e.target.value) || 3000 })} style={{ ...inpSm, width: 70 }} title="Символов" />
            {r.status === "generating" && <span style={{ color: "#6366f1", fontSize: 12 }}>⏳ пишу...</span>}
            {r.status === "done" && <span style={{ color: "#22c55e", fontSize: 12, cursor: "pointer" }} onClick={() => setExpandedId(r.id)}>✅ 👁</span>}
            {r.status === "error" && <span style={{ color: "#dc2626", fontSize: 12 }}>❌</span>}
            {(r.status === "done" || r.status === "error") && <button style={{ ...btnSm, color: "#f59e0b", fontSize: 11 }} onClick={() => updateRow(r.id, { status: "", result: "", date: "" })} title="Очистити результат для перегенерації">↻</button>}
            <button style={{ ...btnSm, color: "#dc2626", fontSize: 11 }} onClick={() => delRow(r.id)}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label style={{ fontSize: 10, color: t.textMuted }}>Анкор 1</label>
              <AnchorInput value={r.anchor1} suggestions={semKw} posMap={posMap} style={inpSm} t={t} dark={dark}
                onText={v => updateRow(r.id, { anchor1: v })} onPick={(kw, url) => updateRow(r.id, { anchor1: kw, url1: url })} />
            </div>
            <div style={{ flex: 2, minWidth: 200 }}>
              <label style={{ fontSize: 10, color: t.textMuted }}>URL 1</label>
              <input value={r.url1} onChange={e => updateRow(r.id, { url1: e.target.value })} placeholder="https://..." style={inpSm} />
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label style={{ fontSize: 10, color: t.textMuted }}>Анкор 2</label>
              <AnchorInput value={r.anchor2} suggestions={semKw} posMap={posMap} style={inpSm} t={t} dark={dark}
                onText={v => updateRow(r.id, { anchor2: v })} onPick={(kw, url) => updateRow(r.id, { anchor2: kw, url2: url })} />
            </div>
            <div style={{ flex: 2, minWidth: 200 }}>
              <label style={{ fontSize: 10, color: t.textMuted }}>URL 2</label>
              <input value={r.url2} onChange={e => updateRow(r.id, { url2: e.target.value })} placeholder="https://..." style={inpSm} />
            </div>
          </div>
        </div>
      ))}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button style={btnSm} onClick={addRow}>+ Строка</button>
        <input type="number" min={1} max={100} value={addN} onChange={e => setAddN(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))} style={{ ...inp, fontSize: 12, padding: "6px 8px", width: 56 }} title="Скільки рядків додати" />
        <button style={btnSm} onClick={() => setRows(prev => [...prev, ...Array.from({ length: addN }, () => emptyRow())])}>+ Додати {addN}</button>
        <button style={btnP} onClick={runAll} disabled={runRef.current}>
          {runRef.current ? "⏳ Генерация..." : "🚀 Сгенерировать все"}
        </button>
        <button style={btnG} onClick={() => { const done = rows.filter((r) => r.status === "done" && r.result); setSelectedIds((prev) => prev.size === done.length && done.length ? new Set() : new Set(done.map((r) => r.id))); }}>
          ☑ Выбрать готовые
        </button>
        <button style={btnG} onClick={exportSelectedCsv} title="Выгрузить выбранные готовые статьи в CSV-шаблон публикаций (макс. 150)">
          ⬇ CSV публикаций{selectedIds.size ? " (" + selectedIds.size + ")" : ""}
        </button>
        <button style={{ ...btnG, marginLeft: "auto" }} onClick={() => setShowAnchorBank(!showAnchorBank)}>
          🏦 Банк анкорів ({anchorBank.length})
        </button>
      </div>

      {/* Anchor Bank panel */}
      {showAnchorBank && (
        <div style={{ ...card, padding: 16, marginTop: 0, border: `1px solid ${dark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 14 }}>🏦</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Банк анкорів</span>
            <span style={{ fontSize: 10, color: t.textMuted }}>Натисніть на анкор щоб вставити в порожній рядок</span>
            <button onClick={() => setShowAnchorBank(false)} style={{ ...btnSm, marginLeft: "auto" }}>✕</button>
          </div>
          <input value={anchorSearch} onChange={e => setAnchorSearch(e.target.value)} placeholder="Пошук анкора..."
            style={{ ...inp, fontSize: 11, padding: "5px 10px", marginBottom: 8 }} />
          {anchorBank.length === 0 && <div style={{ textAlign: "center", padding: 16, color: t.textMuted, fontSize: 12 }}>Банк порожній. Згенеруйте статті — анкори збережуться автоматично.</div>}
          <div style={{ maxHeight: 250, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
            {anchorBank
              .filter(a => !anchorSearch || a.anchor.toLowerCase().includes(anchorSearch.toLowerCase()) || a.url.toLowerCase().includes(anchorSearch.toLowerCase()))
              .map(a => (
              <div key={a.id} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6,
                background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`,
                cursor: "pointer",
              }}
                onClick={() => {
                  // Find first empty anchor slot
                  const emptyIdx = rows.findIndex(r => (!r.anchor1 && !r.url1) || (!r.anchor2 && !r.url2));
                  if (emptyIdx >= 0) {
                    const r = rows[emptyIdx];
                    if (!r.anchor1 && !r.url1) {
                      setRows(prev => prev.map((rr, i) => i === emptyIdx ? { ...rr, anchor1: a.anchor, url1: a.url } : rr));
                    } else if (!r.anchor2 && !r.url2) {
                      setRows(prev => prev.map((rr, i) => i === emptyIdx ? { ...rr, anchor2: a.anchor, url2: a.url } : rr));
                    }
                    flash(`"${a.anchor}" вставлено в рядок ${emptyIdx + 1}`);
                  } else {
                    // Add new row with this anchor
                    setRows(prev => [...prev, { ...emptyRow(), anchor1: a.anchor, url1: a.url }]);
                    flash(`"${a.anchor}" додано в новий рядок`);
                  }
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 600, color: "#6366f1", flex: 1 }}>{a.anchor}</span>
                <span style={{ fontSize: 10, color: t.textMuted, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.url}</span>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                  background: a.times_used >= 3 ? "rgba(239,68,68,0.1)" : a.times_used >= 2 ? "rgba(245,158,11,0.1)" : "rgba(16,185,129,0.1)",
                  color: a.times_used >= 3 ? "#ef4444" : a.times_used >= 2 ? "#f59e0b" : "#10b981",
                }}>×{a.times_used}</span>
                <button onClick={e => { e.stopPropagation(); apiFetch(`/api/projects/${activeId}/anchors/${a.id}`, { method: "DELETE" }); setAnchorBank(prev => prev.filter(x => x.id !== a.id)); }}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: dark ? "#555" : "#ccc", padding: "2px" }}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress */}
      {runRef.current && (() => {
        const total = rows.filter(r => r.topic && r.anchor1 && r.url1).length;
        const done = rows.filter(r => r.status === "done" || r.status === "error").length;
        return (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, marginBottom: 4, color: "#6366f1" }}>⏳ {done}/{total}</div>
            <div style={{ height: 6, background: dark ? "#333" : "#e5e7eb", borderRadius: 3 }}>
              <div style={{ height: 6, width: (total ? done / total * 100 : 0) + "%", background: "#6366f1", borderRadius: 3, transition: "width 0.3s" }} />
            </div>
          </div>
        );
      })()}

      {/* Preview modal */}
      {expandedId && (() => {
        const r = rows.find(x => x.id === expandedId);
        if (!r?.result) return null;
        const chars = r.result.replace(/<[^>]*>/g, "").length;
        const links = (r.result.match(/<a /g) || []).length;
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={() => setExpandedId(null)}>
            <div style={{ background: dark ? "#1a1a2e" : "#fff", borderRadius: 16, padding: 28, maxWidth: 800, width: "100%", maxHeight: "85vh", overflowY: "auto", color: dark ? "#e0e0e0" : "#333" }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{r.topic}</h2>
                  {r.date && <span style={{ fontSize: 11, color: t.textMuted }}>📅 {r.date}</span>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={btnSm} onClick={() => { copyToClipboard(r.result); }}>📋 HTML</button>
                  <button style={btnSm} onClick={() => setExpandedId(null)}>✕</button>
                </div>
              </div>

              {/* Rendered preview with styles */}
              <style dangerouslySetInnerHTML={{ __html: `
                .article-preview h2 { font-size: 20px; font-weight: 700; color: ${dark ? "#e0e0e0" : "#1a1a2e"}; margin: 24px 0 12px; border-bottom: 2px solid #6366f1; padding-bottom: 8px; }
                .article-preview h3 { font-size: 16px; font-weight: 600; color: ${dark ? "#c0c0d0" : "#333"}; margin: 18px 0 8px; }
                .article-preview p { margin: 10px 0; line-height: 1.8; }
                .article-preview ul, .article-preview ol { margin: 10px 0; padding-left: 24px; }
                .article-preview li { margin: 4px 0; line-height: 1.6; }
                .article-preview a { color: #6366f1; text-decoration: underline; font-weight: 500; }
                .article-preview a:hover { color: #4f46e5; }
              `}} />
              <div className="article-preview" style={{ lineHeight: 1.7, fontSize: 14, padding: 20, background: dark ? "#0f0f14" : "#fff", borderRadius: 8, marginBottom: 14, border: `1px solid ${dark ? "#2a2a3a" : "#e5e7eb"}`, maxHeight: 500, overflowY: "auto" }}
                dangerouslySetInnerHTML={{ __html: r.result }} />

              {/* Stats */}
              <div style={{ display: "flex", gap: 16, fontSize: 12, marginBottom: 14, color: t.textMuted }}>
                <span>📊 <b>{chars}</b> символов</span>
                <span>🔗 <b>{links}</b> ссылок</span>
              </div>

              {/* HTML source */}
              <details>
                <summary style={{ fontSize: 12, color: "#6366f1", cursor: "pointer", fontWeight: 600 }}>{"</>"} HTML-код</summary>
                <div style={{ position: "relative", marginTop: 6 }}>
                  <button style={{ ...btnSm, position: "absolute", top: 6, right: 6, fontSize: 10 }}
                    onClick={() => { copyToClipboard(r.result); }}>📋 Копировать</button>
                  <pre style={{ fontSize: 11, background: dark ? "#0a0a12" : "#f3f4f6", padding: 14, borderRadius: 8, overflowX: "auto", maxHeight: 300, whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.5, color: dark ? "#a6e3a1" : "#333" }}>{r.result}</pre>
                </div>
              </details>
            </div>
          </div>
        );
      })()}

      {/* Done articles list */}
      {rows.some(r => r.result) && (
        <div style={{ marginTop: 14 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>📄 Готовые статьи ({rows.filter(r => r.result).length})</h3>
          {rows.filter(r => r.result).map(r => {
            const chars = r.result.replace(/<[^>]*>/g, "").length;
            const links = (r.result.match(/<a /g) || []).length;
            const isOpen = expandedId === r.id;
            return (
              <div key={r.id} style={{ ...card, marginBottom: 8, padding: 0, overflow: "hidden" }}>
                {/* Header - always visible */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", cursor: "pointer", background: isOpen ? (dark ? "#1a1a2e" : "#f0f4ff") : undefined }}
                  onClick={() => setExpandedId(isOpen ? null : r.id)}>
                  <span style={{ fontSize: 14 }}>{isOpen ? "▼" : "▶"}</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{r.topic}</span>
                    <div style={{ fontSize: 10, color: t.textMuted, marginTop: 2 }}>
                      {r.date && <span>📅 {r.date} · </span>}
                      {chars} симв. · {links} ссылок
                    </div>
                  </div>
                  <button style={btnSm} onClick={e => { e.stopPropagation(); copyToClipboard(r.result); }}>📋 Копировать</button>
                </div>

                {/* Expanded content */}
                {isOpen && (
                  <div style={{ padding: 14 }}>
                    {/* Styled preview */}
                    <style dangerouslySetInnerHTML={{ __html: `
                      .art-pv-${r.id} h2 { font-size: 18px; font-weight: 700; color: ${dark ? "#e0e0e0" : "#1a1a2e"}; margin: 20px 0 10px; border-bottom: 2px solid #6366f1; padding-bottom: 6px; }
                      .art-pv-${r.id} h3 { font-size: 15px; font-weight: 600; color: ${dark ? "#c0c0d0" : "#333"}; margin: 16px 0 6px; }
                      .art-pv-${r.id} p { margin: 8px 0; line-height: 1.8; }
                      .art-pv-${r.id} ul, .art-pv-${r.id} ol { margin: 8px 0; padding-left: 24px; }
                      .art-pv-${r.id} li { margin: 3px 0; line-height: 1.6; }
                      .art-pv-${r.id} a { color: #6366f1; text-decoration: underline; font-weight: 500; }
                    `}} />
                    <div className={`art-pv-${r.id}`} style={{ fontSize: 14, lineHeight: 1.7, padding: 16, background: dark ? "#0f0f14" : "#fff", borderRadius: 8, border: `1px solid ${dark ? "#2a2a3a" : "#e5e7eb"}`, maxHeight: 500, overflowY: "auto" }}
                      dangerouslySetInnerHTML={{ __html: r.result }} />

                    {/* HTML source below */}
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#6366f1" }}>{"</>"} HTML-код</span>
                        <button style={{ ...btnSm, fontSize: 10 }} onClick={() => copyToClipboard(r.result)}>📋 Копировать HTML</button>
                      </div>
                      <pre style={{ fontSize: 11, background: dark ? "#0a0a12" : "#f3f4f6", padding: 14, borderRadius: 8, overflowX: "auto", maxHeight: 250, whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.5, color: dark ? "#a6e3a1" : "#333" }}>{r.result}</pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      )}

      {/* ── Archive by months ── */}
      {archive.length > 0 && (() => {
        // Group by month
        const months = {};
        const monthNames = ["Січень","Лютий","Березень","Квітень","Травень","Червень","Липень","Серпень","Вересень","Жовтень","Листопад","Грудень"];
        archive.forEach(a => {
          let d;
          if (a.date && a.date.includes(".")) {
            // "20.03.2026, 15:30" format
            const parts = a.date.split(/[.,\s/]/);
            d = new Date(parts[2], parseInt(parts[1]) - 1, parts[0]);
          } else if (a.date) {
            d = new Date(a.date);
          } else {
            d = new Date();
          }
          if (isNaN(d.getTime())) d = new Date();
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
          if (!months[key]) months[key] = { label, items: [] };
          months[key].items.push(a);
        });
        const sorted = Object.entries(months).sort((a, b) => b[0].localeCompare(a[0]));

        return (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>📁 Архів статей</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sorted.map(([key, month]) => (
                <div key={key} style={{ ...card, margin: 0, padding: 0, overflow: "hidden" }}>
                  <div
                    onClick={() => setArchiveOpen(archiveOpen === key ? null : key)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer",
                      background: archiveOpen === key ? (dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.03)") : "transparent",
                    }}
                  >
                    <span style={{ fontSize: 11, color: archiveOpen === key ? "#6366f1" : "#888", transform: archiveOpen === key ? "rotate(90deg)" : "none", transition: "transform .2s" }}>▶</span>
                    <span style={{ fontSize: 16 }}>📁</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{month.label}</span>
                    <span style={{ fontSize: 11, color: t.textMuted, padding: "1px 8px", borderRadius: 8, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }}>{month.items.length} статей</span>
                  </div>
                  {archiveOpen === key && (
                    <div style={{ padding: "0 16px 12px" }}>
                      {month.items.map((a, i) => (
                        <div key={a.id || i} style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, marginTop: 4,
                          background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                          border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`,
                        }}>
                          <span style={{ fontSize: 12 }}>📄</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.topic || "Без теми"}</div>
                            <div style={{ fontSize: 10, color: t.textMuted }}>
                              {a.date || "—"}
                              {a.result && ` · ${a.result.replace(/<[^>]*>/g, "").length} симв.`}
                              {a.url1 && ` · ${a.anchor1}`}
                            </div>
                          </div>
                          {a.result && (
                            <button onClick={() => {
                              const w = window.open("", "_blank");
                              w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${a.topic}</title><style>body{font-family:Arial,sans-serif;max-width:800px;margin:40px auto;padding:0 20px;line-height:1.7;color:#333}h2{color:#1a1a2e}a{color:#6366f1}</style></head><body>${a.result}</body></html>`);
                              w.document.close();
                            }} style={btnSm}>👁</button>
                          )}
                          {a.result && (
                            <button onClick={() => { navigator.clipboard.writeText(a.result); flash("HTML скопійовано"); }} style={btnSm}>📋</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Anchor input with autocomplete from project semantics (keyword → fills the matching URL).
function AnchorInput({ value, onText, onPick, suggestions, posMap, style, t, dark }) {
  const posColor = (p) => p == null ? t.textMuted : p <= 3 ? "#10b981" : p <= 10 ? "#6366f1" : p <= 30 ? "#f59e0b" : "#ef4444";
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [rect, setRect] = useState(null);
  const matches = useMemo(() => {
    const q = String(value || "").trim().toLowerCase();
    if (!q || !suggestions || !suggestions.length) return [];
    // Ранжуємо: точний збіг → починається з запиту → слово-межа → решта. Щоб точний ключ не зрізало slice(12).
    const rank = (kw) => { const k = kw.toLowerCase(); if (k === q) return 0; if (k.startsWith(q + " ") || k.startsWith(q)) return 1; if ((" " + k).includes(" " + q)) return 2; return 3; };
    return suggestions
      .filter(x => x.keyword.toLowerCase().includes(q))
      .map(x => ({ x, r: rank(x.keyword), len: x.keyword.length }))
      .sort((a, b) => a.r - b.r || a.len - b.len)
      .slice(0, 12)
      .map(o => o.x);
  }, [value, suggestions]);
  const refresh = () => { if (ref.current) setRect(ref.current.getBoundingClientRect()); };
  return (
    <div style={{ position: "relative" }}>
      <input ref={ref} value={value} placeholder="анкор / ключ із семантики"
        onChange={e => { onText(e.target.value); refresh(); setOpen(true); }}
        onFocus={() => { refresh(); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={style} />
      {open && matches.length > 0 && rect && createPortal(
        <div style={{ position: "fixed", top: rect.bottom + 2, left: rect.left, width: rect.width, zIndex: 99999, maxHeight: 260, overflowY: "auto", background: dark ? "#15151f" : "#fff", border: `1px solid ${t.border}`, borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.35)" }}>
          {matches.map((m, i) => {
            const pos = posMap ? posMap[String(m.keyword).toLowerCase().trim()] : undefined;
            return (
            <div key={i} onMouseDown={e => { e.preventDefault(); onPick(m.keyword, m.url); setOpen(false); }}
              style={{ padding: "7px 10px", cursor: "pointer", borderBottom: `1px solid ${dark ? "#222230" : "#f1f1f4"}` }}>
              <div style={{ fontSize: 12, color: t.text, fontWeight: 500, display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.keyword}</span>
                {pos !== undefined && <span title="Поточна позиція цього ключа" style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, color: posColor(pos), background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", borderRadius: 4, padding: "0 5px" }}>#{pos == null ? "—" : pos}</span>}
              </div>
              <div style={{ fontSize: 10, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.url}</div>
            </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

// ═══ GENERATE META VIEW ═══
